import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import {
  reservationBoardService,
  type ReservationBoard,
  type ReservationBooking,
} from '../services/reservation-system/reservation-board.service';
import { reservationPublicService } from '../services/reservation-system/reservation-public.service';
import type { RsCountryCode } from '../constants/rsCountryCodes';
import { getErrorResponseStatus } from '../utils/error.utils';
import { extractReservationErrorMessage } from '../utils/reservationApiError.util';
import { filterResourceNamesByBoard } from '../utils/reservationBoardFormPrefs.util';
import {
  createNextRsBookingRangeAfterCreate,
  revealRsBookingCalendarDay,
  toRsApiDateTime,
} from '../utils/reservationBoardCalendar.util';

type FlashFns = {
  flashSuccess: (text: string) => void;
  flashError: (text: string) => void;
  clearFlash: () => void;
};

type BookingFormSlice = {
  selectedResourceNames: string[];
  startDateTime: string;
  endDateTime: string;
  displayName: string;
  notes: string;
  deletePassword: string;
  setStartDateTime: (value: string) => void;
  setEndDateTime: (value: string) => void;
  setDeletePassword: (value: string) => void;
  setSelectedResourceNames: (value: string[]) => void;
  persistFormPrefs: (overrides?: Partial<{ resourceNames: string[] }>) => void;
};

type ResourceEditSlice = {
  editResourceIndex: number | null;
  setEditResourceIndex: (value: number | null) => void;
  setEditResourceName: (value: string) => void;
};

type DeleteModalSlice = {
  setDeleteTarget: (value: ReservationBooking | null) => void;
  setDeletePasswordInput: (value: string) => void;
  setShowOverlapModal: (value: boolean) => void;
  /** 削除モーダル内エラー（backdrop 裏の flash を使わない） */
  setDeleteErrorMessage: (value: string | null) => void;
};

/**
 * 共有予約表の create / delete / share / resource / country mutations。
 * ページ本体は表示とフォーム状態に集中させる。
 */
export function useRsBoardShareMutations({
  shareToken,
  boardId,
  board,
  t,
  flash,
  form,
  resourceEdit,
  deleteModal,
  onAfterCreate,
}: {
  shareToken: string;
  boardId: number;
  board: ReservationBoard | undefined;
  t: TFunction;
  flash: FlashFns;
  form: BookingFormSlice;
  resourceEdit: ResourceEditSlice;
  deleteModal: DeleteModalSlice;
  /** 登録成功後（次枠へ進めたあと）。カレンダー下書きの一時非表示などに使う */
  onAfterCreate?: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const invalidateBookings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['reservation-bookings-share', shareToken] });
  }, [queryClient, shareToken]);

  const mutationError = useCallback(
    (key: string, fallback: string) => (err: unknown) => {
      flash.flashError(extractReservationErrorMessage(err, t, t(key, { defaultValue: fallback })));
    },
    [flash, t]
  );

  const createMutation = useMutation({
    mutationFn: () =>
      reservationPublicService.createBookings(shareToken, {
        resourceNames: form.selectedResourceNames,
        startTime: toRsApiDateTime(form.startDateTime),
        endTime: toRsApiDateTime(form.endDateTime),
        displayName: form.displayName.trim(),
        notes: form.notes.trim() || undefined,
        deletePassword: form.deletePassword.trim() || undefined,
      }),
    onSuccess: (created) => {
      const bookedDay = Number(form.startDateTime.slice(8, 10));
      flash.flashSuccess(
        created.length > 1
          ? t('reservation.board.registeredMulti', {
              defaultValue: '{{count}}件の項目を登録しました。',
              count: created.length,
            })
          : t('reservation.board.registered', { defaultValue: '登録しました。' })
      );
      form.persistFormPrefs();
      const next = createNextRsBookingRangeAfterCreate(form.endDateTime);
      form.setStartDateTime(next.start);
      form.setEndDateTime(next.end);
      form.setDeletePassword('');
      invalidateBookings();
      // 狭い画面は表に戻して登録結果を見せる（次枠の下書きはフォームに残る）
      revealRsBookingCalendarDay(bookedDay);
      onAfterCreate?.();
    },
    onError: (err) => {
      if (getErrorResponseStatus(err) === 409) {
        // クライアント未把握の衝突（他端末登録など）→ 表を更新して見える化する
        invalidateBookings();
        deleteModal.setShowOverlapModal(true);
        flash.clearFlash();
        const day = Number(form.startDateTime.slice(8, 10));
        revealRsBookingCalendarDay(day);
        return;
      }
      mutationError('reservation.board.registerFailed', '登録に失敗しました。')(err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ bookingId, password }: { bookingId: number; password?: string }) =>
      reservationPublicService.deleteBooking(shareToken, bookingId, password),
    onSuccess: () => {
      flash.flashSuccess(t('reservation.board.deleted', { defaultValue: '削除しました。' }));
      deleteModal.setDeleteTarget(null);
      deleteModal.setDeletePasswordInput('');
      deleteModal.setDeleteErrorMessage(null);
      invalidateBookings();
    },
    onError: (err, variables) => {
      const message = extractReservationErrorMessage(
        err,
        t,
        t('reservation.board.deleteFailed', { defaultValue: '削除に失敗しました。' })
      );
      // パスワードモーダル経由（password キーあり）はモーダル内表示。即時削除は flash。
      if ('password' in variables) {
        deleteModal.setDeleteErrorMessage(message);
        flash.clearFlash();
        return;
      }
      deleteModal.setDeleteErrorMessage(null);
      flash.flashError(message);
    },
  });

  const issueShareMutation = useMutation({
    mutationFn: () => reservationBoardService.issueShareToken(boardId),
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: ['reservation-board-share', shareToken] });
      navigate(`/reservations/share/${result.shareToken}`, { replace: true });
      flash.flashSuccess(t('reservation.board.shareUrlIssued', { defaultValue: '共有URLを発行しました。' }));
    },
    onError: mutationError('reservation.board.shareUrlIssueFailed', '共有URLの発行に失敗しました。'),
  });

  const revokeShareMutation = useMutation({
    mutationFn: () => reservationBoardService.revokeShareToken(boardId),
    onSuccess: () => {
      queryClient.setQueryData(['reservation-board-share', shareToken], (prev: typeof board) =>
        prev ? { ...prev, shareUrl: null } : prev
      );
      flash.flashSuccess(t('reservation.board.shareUrlRevoked', { defaultValue: '共有を停止しました。' }));
    },
    onError: mutationError('reservation.board.shareUrlRevokeFailed', '共有の停止に失敗しました。'),
  });

  const updateResourceMutation = useMutation({
    mutationFn: (nextResourceNames: string[]) =>
      reservationBoardService.updateBoard(boardId, { resourceNames: nextResourceNames }),
    onSuccess: (updatedBoard) => {
      flash.flashSuccess(t('reservation.board.resourcesUpdated', { defaultValue: '項目を更新しました。' }));
      let nextSelected = form.selectedResourceNames;
      if (resourceEdit.editResourceIndex !== null) {
        const oldName = board?.resourceNames[resourceEdit.editResourceIndex];
        const newName = updatedBoard.resourceNames[resourceEdit.editResourceIndex];
        if (oldName && newName && nextSelected.includes(oldName)) {
          nextSelected = nextSelected.map((name) => (name === oldName ? newName : name));
        }
      }
      nextSelected = filterResourceNamesByBoard(nextSelected, updatedBoard.resourceNames);
      form.setSelectedResourceNames(nextSelected);
      form.persistFormPrefs({ resourceNames: nextSelected });
      resourceEdit.setEditResourceIndex(null);
      resourceEdit.setEditResourceName('');
      queryClient.setQueryData(['reservation-board-share', shareToken], updatedBoard);
      queryClient.invalidateQueries({ queryKey: ['reservation-bookings-share', shareToken] });
    },
    onError: mutationError('reservation.board.resourceUpdateFailed', '項目名の更新に失敗しました。'),
  });

  const updateCountryMutation = useMutation({
    mutationFn: (countryCode: RsCountryCode) =>
      reservationBoardService.updateBoard(boardId, { countryCode }),
    onSuccess: (updatedBoard) => {
      queryClient.setQueryData(['reservation-board-share', shareToken], updatedBoard);
      queryClient.invalidateQueries({ queryKey: ['reservation-holidays-share', shareToken] });
    },
    onError: mutationError('reservation.board.countryUpdateFailed', '国の設定の更新に失敗しました。'),
  });

  return {
    createMutation,
    deleteMutation,
    issueShareMutation,
    revokeShareMutation,
    updateResourceMutation,
    updateCountryMutation,
  };
}
